import { Link } from 'react-router-dom';
import { COMPANY } from '@jt/domain';
import { OutboundLink } from './LeavingDialog';
import { useSession } from '../lib/session';

/**
 * The footer, on a diet.
 *
 * ## Why it shrank
 *
 * It was four columns — who we are, help, contact details, session — on every
 * route. Four columns is a landing page's footer, and this is a console
 * somebody has open all day. On a short page it took a third of the viewport,
 * and on Contracts mid-load it was the only thing on screen underneath a
 * spinner, which makes an application look like it has finished when it has
 * not started.
 *
 * ## What survived, and why those
 *
 * A footer on an internal tool is a floor, not a directory. It is there so a
 * page ends rather than stops, and so somebody who has scrolled to the bottom
 * has somewhere to go. That needs a line, not a grid.
 *
 * Help stays because it is the reason anybody looks down here. The timezone
 * note stays because every timestamp on every page depends on it and it is
 * written nowhere else. The address, the phone number and the office details
 * went: they are on the Help page, which is one click away and is where
 * somebody looking for them would go first.
 *
 * "This session" went to the account menu, which is where a person looks for
 * who they are signed in as — it is account state, not page furniture, and it
 * was the one thing down here that nobody scrolls to find.
 */
export function Footer() {
  const { can } = useSession();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-10 border-t border-border bg-card">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-4 text-xs text-muted-foreground md:px-6">
        <p>
          &copy; {year} {COMPANY.legalName}
        </p>

        {/*
          Ordered by how often somebody needs it, not alphabetically. Status is
          capability-filtered like the rail — a link to something that will
          refuse to open is worse than no link.
        */}
        <nav className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link to="/help" className="hover:text-navy-700 hover:underline">
            How things work
          </Link>
          <Link to="/whats-new" className="hover:text-navy-700 hover:underline">
            What&rsquo;s new
          </Link>
          {can('system:status') && (
            <Link to="/status" className="hover:text-navy-700 hover:underline">
              System status
            </Link>
          )}
          {/* Confirms before leaving — a dispatcher mid-ticket should not lose
              what they typed. */}
          <OutboundLink href={COMPANY.termsUrl} className="hover:text-navy-700 hover:underline">
            Terms and privacy
          </OutboundLink>
        </nav>

        {/*
          Kept, small as it is. Every time on every screen is in this zone and
          it is stated nowhere else in the application — so a report read in
          another country is read wrongly without it.
        */}
        <p>Times in {COMPANY.town} time &middot; EAT</p>
      </div>
    </footer>
  );
}
